import React, { useState, useEffect, useCallback, useRef, useSyncExternalStore } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from '../hooks/useTranslation';
import { useSip } from '../hooks/useSip';
import { NativeBridge } from '../native/NativeBridge';
import { API_ENDPOINTS } from '@caro/shared/constants';
import { speakOrBeep } from '@caro/shared/utils';
import * as RemoteEmergency from '../services/RemoteEmergency';
import type { Language } from '../i18n/types';

const BEEP_FREQUENCY = 800;
const BEEP_DURATION = 150;

/**
 * Auto-cancel window on the emergency CONFIRMATION screen (accidental-press
 * guard). At {@link CONFIRM_TTS_WARNING_AT_MS} a localized "5 seconds remaining"
 * TTS cue fires (safety-critical, hard-coded on).
 */
const CONFIRM_AUTOCANCEL_MS = 20_000;
const CONFIRM_TTS_WARNING_AT_MS = 15_000;

/** Map app language codes to BCP-47 tags for the TTS utterance. */
const SPEECH_LANG_MAP: Record<Language, string> = {
  en: 'en-US',
  nl: 'nl-NL',
  fr: 'fr-FR',
  de: 'de-DE',
};

/** Play an audio beep using Web Audio API */
function playBeep(frequency = BEEP_FREQUENCY, duration = BEEP_DURATION) {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.connect(gain);
    gain.connect(ctx.destination);
    oscillator.frequency.value = frequency;
    oscillator.type = 'square';
    gain.gain.value = 0.3;
    oscillator.start();
    setTimeout(() => { oscillator.stop(); ctx.close(); }, duration);
  } catch {
    // Audio not available
  }
}

/**
 * Emergency screen.
 *
 * Flow (the API / automation engine owns the countdown — the tablet reflects it):
 *  1. Resident confirms (Ja) → POST `tabletToAutomation` webhook directly →
 *     "Alarm sent" screen.
 *  2. The automation puts the tablet into ALARM MODE via an `emergency` MQTT
 *     command (`RemoteEmergency` store) → the tablet counts the given seconds
 *     down to 0 and FREEZES at 0.
 *  3. While counting down, Cancel is shown; once it hits 0, Cancel is hidden.
 *  4. When emergency services CALL the tablet (inbound SIP), Cancel reappears.
 *  x. Cancel ALWAYS fires the `resetTabletToAutomation` webhook (+ a unit-wide
 *     alarm reset); the automation's reset block then returns every tablet in
 *     the unit to normal via an `emergency`/`reset` command.
 *
 * `?remote=1` (automation-triggered on another device's alarm): skip the
 * confirm + the local webhook — the originating automation already owns the
 * alarm; the tablet just shows the alarm-mode countdown.
 */
type LocalStatus = 'confirming' | 'sent' | 'cancelled';

export function EmergencyPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const remote = searchParams.get('remote') === '1';
  const { user, api } = useAuth();
  const { t, lang } = useTranslation();
  const { currentCall, answerCall } = useSip();

  const emergency = useSyncExternalStore(RemoteEmergency.subscribe, RemoteEmergency.getState);
  const [status, setStatus] = useState<LocalStatus>(remote ? 'sent' : 'confirming');
  const [countdown, setCountdown] = useState(0);
  const [callSeen, setCallSeen] = useState(false);
  const epochRef = useRef<number>(-1);

  const personId = (user as any)?.personId ?? (user as any)?.person?.id ?? '';

  // Hide status bar on mount, restore on unmount.
  useEffect(() => {
    NativeBridge.hideStatusBar();
    return () => { NativeBridge.showStatusBar(); };
  }, []);

  // (Re)start the countdown whenever the automation (re)enters alarm mode.
  useEffect(() => {
    if (!emergency.active) return;
    if (epochRef.current === emergency.epoch) return;
    epochRef.current = emergency.epoch;
    setCountdown(emergency.seconds);
    setCallSeen(false);
  }, [emergency.active, emergency.epoch, emergency.seconds]);

  // Tick down to 0 then FREEZE (no further ticks at 0).
  useEffect(() => {
    if (!emergency.active || countdown <= 0) return;
    playBeep(BEEP_FREQUENCY, BEEP_DURATION);
    NativeBridge.vibrate();
    const timer = setTimeout(() => setCountdown((c) => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(timer);
  }, [emergency.active, countdown]);

  // When emergency services CALL the tablet, auto-answer (hands-free) and mark
  // the call seen so the Cancel button reappears.
  useEffect(() => {
    if (!emergency.active) return;
    const cs = currentCall?.state;
    if (cs === 'ringing_in') {
      setCallSeen(true);
      void answerCall();
    } else if (cs === 'active') {
      setCallSeen(true);
    }
  }, [emergency.active, currentCall?.state, answerCall]);

  // Confirmation auto-cancel + 5s TTS cue (local confirm screen only).
  useEffect(() => {
    if (remote || status !== 'confirming') return;
    const autoCancel = setTimeout(() => { setStatus('cancelled'); navigate('/'); }, CONFIRM_AUTOCANCEL_MS);
    const ttsWarn = setTimeout(() => {
      void speakOrBeep(t.emergency.fiveSecondsRemaining, { lang: SPEECH_LANG_MAP[lang] || 'en-US' });
    }, CONFIRM_TTS_WARNING_AT_MS);
    return () => { clearTimeout(autoCancel); clearTimeout(ttsWarn); };
  }, [remote, status, navigate, t, lang]);

  // Ja → fire the emergency webhook DIRECTLY; the automation puts the tablet
  // into alarm mode via a command. Show "Alarm sent" meanwhile.
  const handleConfirm = useCallback(() => {
    NativeBridge.hapticImpact('heavy');
    playBeep(600, 200);
    api.post(
      `/api/qbusAutomations/webhook/tabletToAutomation?personId=${encodeURIComponent(String(personId))}`,
      { personId },
    ).catch(() => {});
    setStatus('sent');
  }, [api, personId]);

  // Nee / auto-cancel on the confirm screen — nothing was sent, just dismiss.
  const handleDismiss = useCallback(() => {
    NativeBridge.hapticImpact('light');
    setStatus('cancelled');
    setTimeout(() => navigate('/'), 800);
  }, [navigate]);

  // Cancel (on the sent / alarm screens) ALWAYS fires the reset webhook, then
  // a unit-wide alarm reset, and returns this tablet to normal. The reset
  // automation also resets the unit's other tablets via a command.
  const handleCancel = useCallback(() => {
    NativeBridge.hapticImpact('medium');
    api.post(
      `/api/qbusAutomations/webhook/resetTabletToAutomation?personID=${encodeURIComponent(String(personId))}`,
      { personID: personId, personId },
    ).catch(() => {});
    api.post(API_ENDPOINTS.EMERGENCY_RESET, { resolution: 'handled' }).catch(() => {});
    RemoteEmergency.reset();
    navigate('/');
  }, [api, personId, navigate]);

  const CancelButton = (
    <button
      onClick={handleCancel}
      style={{
        marginTop: '32px',
        minHeight: 'var(--touch-emergency)',
        minWidth: '320px',
        padding: '0 40px',
        backgroundColor: 'white',
        color: 'var(--caro-emergency)',
        fontSize: 'var(--font-heading)',
        fontWeight: 700,
        border: 'none',
        borderRadius: '16px',
      }}
    >
      {t.emergency.buttonCancel}
    </button>
  );

  // --- ALARM MODE (automation-driven countdown → freeze at 0) ---
  if (emergency.active) {
    const cancelVisible = countdown > 0 || callSeen;
    return (
      <FullScreenEmergency>
        <div style={{
          fontSize: 'var(--font-heading)',
          fontWeight: 700,
          marginBottom: '16px',
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1,
        }}>
          {countdown}
        </div>
        <p style={{ fontSize: 'var(--font-body)', textAlign: 'center' }}>
          {t.emergency.alarmActive}
        </p>
        {cancelVisible && CancelButton}
      </FullScreenEmergency>
    );
  }

  // --- ALARM SENT (waiting for the automation to start alarm mode) ---
  if (status === 'sent') {
    return (
      <FullScreenEmergency>
        <h1 style={{ fontSize: 'var(--font-heading)', marginBottom: '24px', textAlign: 'center' }}>
          {t.emergency.alarmSent}
        </h1>
        <Spinner />
        {CancelButton}
      </FullScreenEmergency>
    );
  }

  // --- CONFIRMING (local accidental-press guard) ---
  if (status === 'confirming') {
    return (
      <FullScreenEmergency>
        <h1 style={{ fontSize: 'var(--font-heading)', marginBottom: '24px', textAlign: 'center' }}>
          {t.emergency.confirmationTitle}
        </h1>
        <div style={{ display: 'flex', gap: '24px', width: '100%', maxWidth: '400px' }}>
          <button
            onClick={handleConfirm}
            style={{
              flex: 1, minHeight: 'var(--touch-emergency)',
              backgroundColor: 'var(--caro-emergency)', color: 'white',
              fontSize: 'var(--font-body)', fontWeight: 700,
              border: '3px solid white', borderRadius: '12px',
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', gap: '6px', padding: '12px',
            }}
            aria-label={t.emergency.buttonYes}
          >
            <span aria-hidden="true" style={{ display: 'inline-flex' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" focusable="false">
                <polyline points="4 12 10 18 20 6" />
              </svg>
            </span>
            <span>{t.emergency.buttonYes}</span>
          </button>
          <button
            onClick={handleDismiss}
            style={{
              flex: 1, minHeight: 'var(--touch-emergency)',
              backgroundColor: 'var(--caro-success)', color: 'white',
              fontSize: 'var(--font-body)', fontWeight: 700,
              border: '3px solid white', borderRadius: '12px',
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', gap: '6px', padding: '12px',
            }}
            aria-label={t.emergency.buttonNoCancel}
          >
            <span aria-hidden="true" style={{ display: 'inline-flex' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" focusable="false">
                <line x1="6" y1="6" x2="18" y2="18" />
                <line x1="18" y1="6" x2="6" y2="18" />
              </svg>
            </span>
            <span>{t.emergency.buttonNo}</span>
          </button>
        </div>
      </FullScreenEmergency>
    );
  }

  // --- CANCELLED ---
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '100vh', fontSize: 'var(--font-heading)', color: 'var(--caro-text-light)',
    }}>
      {t.emergency.cancelled}
    </div>
  );
}

function FullScreenEmergency({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      height: '100vh', backgroundColor: 'var(--caro-emergency)', color: 'white', padding: '24px',
    }}>
      {children}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse-ring { 0% { transform: scale(0.8); opacity: 1; } 100% { transform: scale(1.4); opacity: 0; } }
      `}</style>
    </div>
  );
}

function Spinner() {
  return (
    <div style={{ position: 'relative', width: '80px', height: '80px' }}>
      <div style={{
        position: 'absolute', width: '80px', height: '80px',
        border: '4px solid rgba(255,255,255,0.2)', borderTop: '4px solid white',
        borderRadius: '50%', animation: 'spin 1s linear infinite',
      }} />
      <div style={{
        position: 'absolute', width: '80px', height: '80px',
        border: '4px solid rgba(255,255,255,0.3)', borderRadius: '50%',
        animation: 'pulse-ring 1.5s ease-out infinite',
      }} />
    </div>
  );
}
